import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from '@pantry/ui';
import {
  passwordSchema as sharedPasswordSchema,
  getPasswordChecks,
  getPasswordStrength,
} from '@pantry/shared';
import { useAuth } from '../../contexts/useAuth';
import { toast } from 'sonner';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import { KeyIcon } from '@heroicons/react/24/outline';

// Use shared password schema with confirm password validation
const passwordSchema = (t: ReturnType<typeof useLingui>['t']) =>
  z
    .object({
      newPassword: sharedPasswordSchema,
      confirmPassword: z.string(),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t`Passwords don't match`,
      path: ['confirmPassword'],
    });

type PasswordFormData = z.infer<ReturnType<typeof passwordSchema>>;

export function PasswordSection() {
  const { t } = useLingui();
  const { updatePassword } = useAuth();
  const [isChanging, setIsChanging] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema(t)),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data: PasswordFormData) => {
    setIsSubmitting(true);
    try {
      await updatePassword(data.newPassword);
      toast.success(t`Password updated successfully`);
      reset();
      setIsChanging(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to update password`;
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    setIsChanging(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Password validation using shared functions
  const passwordChecks = getPasswordChecks(newPassword);
  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <Card className="rounded-xl" data-tour="settings-security">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <KeyIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans>Security</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Manage your password and account security.</Trans>
          </CardDescription>
        </div>
        {!isChanging && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsChanging(true)}
            className="rounded-xl"
          >
            <KeyIcon className="h-4 w-4 mr-2" />
            <Trans>Change Password</Trans>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isChanging ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                <Trans>New Password</Trans>
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  {...register('newPassword')}
                  placeholder={t`Enter new password`}
                  className={`rounded-xl ${errors.newPassword ? 'border-destructive pr-10' : 'pr-10'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1">
                  {errors.newPassword.message}
                </p>
              )}

              {/* Password strength indicator */}
              {newPassword && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          passwordStrength.strength >= level
                            ? passwordStrength.strength === 1
                              ? 'bg-destructive'
                              : passwordStrength.strength === 2
                                ? 'bg-expiring-soon'
                                : 'bg-fresh'
                            : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <p
                    className={`text-xs font-medium ${
                      passwordStrength.strength === 1
                        ? 'text-destructive'
                        : passwordStrength.strength === 2
                          ? 'text-expiring-soon'
                          : 'text-fresh'
                    }`}
                  >
                    {passwordStrength.label}
                  </p>
                </div>
              )}

              {/* Password requirements */}
              <div className="rounded-xl bg-muted/50 border border-border/50 p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">
                  <Trans>Password must contain:</Trans>
                </p>
                <ul className="space-y-0.5">
                  <li
                    className={`flex items-center gap-1.5 ${passwordChecks.length ? 'text-fresh' : ''}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${passwordChecks.length ? 'bg-fresh' : 'bg-muted-foreground/50'}`}
                    />
                    <Trans>At least 8 characters</Trans>
                  </li>
                  <li
                    className={`flex items-center gap-1.5 ${passwordChecks.uppercase ? 'text-fresh' : ''}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${passwordChecks.uppercase ? 'bg-fresh' : 'bg-muted-foreground/50'}`}
                    />
                    <Trans>One uppercase letter</Trans>
                  </li>
                  <li
                    className={`flex items-center gap-1.5 ${passwordChecks.lowercase ? 'text-fresh' : ''}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${passwordChecks.lowercase ? 'bg-fresh' : 'bg-muted-foreground/50'}`}
                    />
                    <Trans>One lowercase letter</Trans>
                  </li>
                  <li
                    className={`flex items-center gap-1.5 ${passwordChecks.number ? 'text-fresh' : ''}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${passwordChecks.number ? 'bg-fresh' : 'bg-muted-foreground/50'}`}
                    />
                    <Trans>One number</Trans>
                  </li>
                </ul>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                <Trans>Confirm Password</Trans>
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword')}
                  placeholder={t`Confirm new password`}
                  className={`rounded-xl ${errors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="rounded-xl"
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" disabled={isSubmitting} className="rounded-xl">
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <Trans>Updating...</Trans>
                  </div>
                ) : (
                  <Trans>Update Password</Trans>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
              <KeyIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                <Trans>Password</Trans>
              </p>
              <p className="text-sm text-muted-foreground">
                <Trans>Last changed: Unknown</Trans>
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
